/**
 * The deliberately small, portable inspection gate for creator audio — uploaded
 * or provider-generated. One set of MEASUREMENTS, one stored metadata shape, two
 * policies: uploads carry a creator license claim ({@link inspectAudioMedia}),
 * generated candidates additionally face {@link assertGeneratedAudioAcceptable}
 * before they are allowed to exist.
 *
 * ## Two containers, one measurement
 *
 * Every measurement in this module is taken from interleaved PCM16
 * ({@link measureAudioPcm16}) and every budget from
 * {@link audioInspectionFromPcm16}. What differs per container is only how the
 * PCM is *obtained*:
 *
 *  - **`audio/wav`** — canonical PCM16 WAV, parsed HERE by
 *    {@link parseCanonicalWav}. This is the only container a creator may upload
 *    and the only one an SFX provider may return, because a hand-written parser
 *    lets the browser and the trusted server verify precisely the same bytes
 *    without depending on codecs, Web Audio, or native libraries. An upload is
 *    never transcoded: the creator's exact file is the artifact.
 *  - **`audio/mpeg`** — never stored, never uploadable. A MUSIC provider's
 *    reply may arrive as MP3 (Lyria produces nothing else); the worker decodes
 *    it to the same PCM currency and post-processes from there, so MP3 exists
 *    only as a transient source inside the worker (`mp3Source.ts`).
 *  - **`audio/ogg`** — Ogg/Opus, which the WORKER produces for generated music
 *    (plan row 1.4) and decodes with libopus. That decode cannot live in this
 *    package: `@djgg/core` is loaded by the portal and by every game bundle, and
 *    neither should carry a codec. The worker therefore reads its container and
 *    then calls straight into {@link audioInspectionFromPcm16}, so the budgets,
 *    the peak ceiling and the loudness/silence measurements are one
 *    implementation rather than a compressed-audio copy of them.
 *
 * Generated music is the only compressed audio the platform creates. Nothing
 * converts a `audio/wav` row into an `audio/ogg` one, in either direction.
 */

export type AudioMediaKind = 'sfx' | 'music';

/**
 * Containers the platform stores. `audio/wav` is what creators upload and what
 * an SFX provider must return; `audio/ogg` (Ogg/Opus) is what the worker encodes
 * a generated music loop into, because a game bundle should not ship minutes of
 * uncompressed audio to a phone. A music provider's MP3 is decoded, never
 * stored, so it is not a member of this type.
 */
export type AudioMediaMime = 'audio/wav' | 'audio/ogg';

/** File extension per container — the ONE place the bundle's naming is decided. */
const AUDIO_MIME_EXTENSIONS: Readonly<Record<AudioMediaMime, string>> = Object.freeze({
  'audio/wav': 'wav',
  'audio/ogg': 'ogg',
});

/** The file extension one audio container is stored under, everywhere. */
export function audioFileExtension(mime: AudioMediaMime): string {
  return AUDIO_MIME_EXTENSIONS[mime];
}

/**
 * The version-local filename for one audio artifact, derived from its content
 * digest and container. The publisher writes this name and the runtime expects
 * exactly it, so it lives here rather than being spelled out in both.
 */
export function audioBundleFilename(contentSha256: string, mime: AudioMediaMime): string {
  return `audio-${contentSha256}.${audioFileExtension(mime)}`;
}

export interface AudioMediaFormat {
  /** The container a CREATOR may upload for this kind. Generated music is
   *  encoded to `audio/ogg` by the worker and is not an upload contract. */
  readonly uploadMime: 'audio/wav';
  readonly maxBytes: number;
  readonly maxDurationMs: number;
}

/**
 * Per-kind ceilings, applied to every stored artifact whatever its container.
 * The byte ceilings are the shipped UPLOAD limits and are deliberately
 * unchanged: compression does not widen what a creator may send, it only means
 * a generated music loop uses a small fraction of the same ceiling.
 */
export const AUDIO_MEDIA_FORMATS: Readonly<Record<AudioMediaKind, AudioMediaFormat>> = Object.freeze({
  sfx: Object.freeze({ uploadMime: 'audio/wav', maxBytes: 960_044, maxDurationMs: 5_000 }),
  // Each music file is bounded to 6 MiB. As canonical PCM16 WAV that holds only
  // ~35s at 44.1kHz stereo, which is why an UPLOADED long track must be
  // downsampled by its creator; a generated loop is Ogg/Opus and reaches the
  // 120s duration cap inside ~1.5 MiB.
  music: Object.freeze({ uploadMime: 'audio/wav', maxBytes: 6 * 1024 * 1024, maxDurationMs: 120_000 }),
});

export interface AudioMediaLicense {
  /**
   * `creator-owned` / `licensed` are the two UPLOAD claims (the creator owns the
   * recording, or holds a license for it). `provider-generated` is not a claim
   * at all: it records that a generation provider produced the bytes, and the
   * redistribution terms live in the candidate's generator provenance. Upload
   * endpoints reject it — a creator cannot assert it about their own file.
   */
  readonly kind: 'creator-owned' | 'licensed' | 'provider-generated';
  /** Human-readable source of the recording (not a URL requirement). */
  readonly source: string;
  /** Human-readable license or ownership terms. */
  readonly terms: string;
}

export interface AudioMediaLoop {
  readonly loopStartMs: number;
  readonly loopEndMs: number;
}

/**
 * Everything the decoder MEASURES from the exact bytes, with no license or
 * provenance claim attached. One decode produces all of it, so a caller never
 * scans the PCM twice: {@link audioMetadataFrom} projects the STORED subset and
 * adds the license/loop contract, while a generated candidate is additionally
 * judged by {@link assertGeneratedAudioAcceptable} before it is allowed to
 * exist. The measurements that are not stored (loudness, silence) are gate
 * inputs and trace evidence — see {@link AudioMediaMetadata} for why.
 */
export interface AudioBytesInspection {
  readonly mime: AudioMediaMime;
  readonly bytes: number;
  readonly durationMs: number;
  readonly sampleRate: number;
  readonly channels: 1 | 2;
  /** Largest absolute PCM sample, normalized to the signed 16-bit range. */
  readonly peak: number;
  /**
   * Full-file RMS in dBFS — a loudness PROXY, deliberately not called LUFS: it
   * is unweighted and ungated, so it may not be compared to a broadcast target.
   * Floored at {@link AUDIO_SILENCE_DBFS_FLOOR} so silence stays JSON-safe.
   */
  readonly rmsDbfs: number;
  /** Milliseconds before the first frame louder than the silence threshold.
   *  Equals `durationMs` when the file never crosses it. */
  readonly leadingSilenceMs: number;
  /** Milliseconds after the last frame louder than the silence threshold.
   *  Equals `durationMs` when the file never crosses it. */
  readonly trailingSilenceMs: number;
}

/**
 * The metadata STORED beside a digest — deliberately a subset of what the
 * decoder measures.
 *
 * This shape is frozen by an invariant elsewhere: at publication,
 * `manifestClosure` re-inspects the exact bytes it copied into the bundle and
 * demands that the result equal the stored metadata byte for byte. Adding a
 * field here would therefore break every row stored before it — the whole
 * installed base of uploaded audio — on its next build. Loudness and silence
 * are measured on every candidate ({@link AudioBytesInspection}) and recorded in
 * the generation trace; they do not belong in this shape.
 *
 * Compressed generated music widened `mime` to {@link AudioMediaMime} and added
 * NO key, which is exactly why it did not strand the installed base: an
 * uploaded WAV row written before Ogg/Opus existed still says `audio/wav` and
 * still re-inspects to itself byte for byte.
 */
export interface AudioMediaMetadata {
  readonly mime: AudioMediaMime;
  readonly bytes: number;
  readonly durationMs: number;
  readonly sampleRate: number;
  readonly channels: 1 | 2;
  /** Largest absolute PCM sample, normalized to the signed 16-bit range. */
  readonly peak: number;
  readonly license: AudioMediaLicense;
  readonly loopStartMs?: number;
  readonly loopEndMs?: number;
}

/** Trusted publisher output. Paths are relative to one immutable game bundle. */
export interface PublishedAudioAsset {
  url: string;
  contentSha256: string;
  kind: AudioMediaKind;
  audio: AudioMediaMetadata;
}

export class AudioMediaValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AudioMediaValidationError';
    Object.setPrototypeOf(this, AudioMediaValidationError.prototype);
  }
}

const HEADER_BYTES = 44;
const MAX_SOURCE_LENGTH = 512;
const MAX_TERMS_LENGTH = 4_096;
/**
 * The loudest normalized peak any stored artifact may have. The worker's
 * normalize step targets a peak below this on purpose: a lossy encode can
 * overshoot its input by a few percent, and it is the DECODED artifact that has
 * to clear this ceiling.
 */
export const AUDIO_PEAK_CEILING = 0.95;
/** |sample| at or below this counts as silence (≈ -60 dBFS at 16 bits). */
const SILENCE_MAGNITUDE = 32;
/** Reported instead of -Infinity for digital silence, so the measurement can be
 *  stored as JSON and compared numerically. */
export const AUDIO_SILENCE_DBFS_FLOOR = -120;

function fail(message: string): never {
  throw new AudioMediaValidationError(message);
}

function hasAscii(bytes: Uint8Array, offset: number, value: string): boolean {
  return value.length === 4 && value.split('').every((character, index) => bytes[offset + index] === character.charCodeAt(0));
}

function u16(bytes: Uint8Array, offset: number): number {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function u32(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] * 0x1000000)) >>> 0;
}

function inspectLicense(value: unknown): AudioMediaLicense {
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail('Audio license must be an object with kind, source, and terms.');
  const license = value as Record<string, unknown>;
  if (Object.keys(license).length !== 3 || !Object.prototype.hasOwnProperty.call(license, 'kind') ||
    !Object.prototype.hasOwnProperty.call(license, 'source') || !Object.prototype.hasOwnProperty.call(license, 'terms')) {
    fail('Audio license must contain only kind, source, and terms.');
  }
  if (license.kind !== 'creator-owned' && license.kind !== 'licensed' && license.kind !== 'provider-generated') {
    fail('Audio license kind must be creator-owned, licensed, or provider-generated.');
  }
  for (const [field, max] of [['source', MAX_SOURCE_LENGTH], ['terms', MAX_TERMS_LENGTH]] as const) {
    const text = license[field];
    if (typeof text !== 'string' || !text.trim()) fail(`Audio license ${field} must be nonempty.`);
    if (text.length > max) fail(`Audio license ${field} must be at most ${max} characters.`);
  }
  return { kind: license.kind, source: license.source as string, terms: license.terms as string };
}

function inspectLoop(kind: AudioMediaKind, value: unknown, durationMs: number): AudioMediaLoop | undefined {
  if (value === undefined) return undefined;
  if (kind === 'sfx') fail('SFX audio cannot have loop metadata.');
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail('Music loop metadata must contain loopStartMs and loopEndMs.');
  const loop = value as Record<string, unknown>;
  if (Object.keys(loop).length !== 2 || !Object.prototype.hasOwnProperty.call(loop, 'loopStartMs') || !Object.prototype.hasOwnProperty.call(loop, 'loopEndMs')) {
    fail('Music loop metadata must contain only loopStartMs and loopEndMs.');
  }
  if (!Number.isFinite(loop.loopStartMs) || !Number.isInteger(loop.loopStartMs) || !Number.isFinite(loop.loopEndMs) || !Number.isInteger(loop.loopEndMs)) {
    fail('Music loop points must be finite integers in milliseconds.');
  }
  const loopStartMs = loop.loopStartMs as number;
  const loopEndMs = loop.loopEndMs as number;
  if (loopStartMs < 0 || loopEndMs > durationMs || loopStartMs >= loopEndMs) {
    fail('Music loop points must satisfy 0 <= loopStartMs < loopEndMs <= audio duration.');
  }
  return { loopStartMs, loopEndMs };
}

/** Interleaved PCM16 read out of a container, with the frame geometry needed to
 *  measure it. The one currency {@link measureAudioPcm16} and
 *  {@link audioInspectionFromPcm16} deal in. */
export interface AudioPcm16 {
  /** Interleaved signed 16-bit samples: `frame * channels + channel`. */
  readonly samples: Int16Array;
  readonly channels: 1 | 2;
  readonly sampleRate: number;
}

/** What one pass over the PCM measures. No container, license or budget in it. */
export interface AudioPcmMeasurement {
  readonly durationMs: number;
  readonly peak: number;
  readonly rmsDbfs: number;
  readonly leadingSilenceMs: number;
  readonly trailingSilenceMs: number;
}

/**
 * The byte ceiling for a kind. Container-agnostic: the ceiling is a property of
 * the kind, not the encoding.
 *
 * Called twice per artifact, on purpose and from one implementation: each
 * container reader calls it BEFORE parsing or decoding, so an over-budget payload
 * is refused without being expanded into memory, and
 * {@link audioInspectionFromPcm16} re-asserts it as the invariant no caller can
 * skip.
 */
export function assertAudioByteBudget(kind: AudioMediaKind, byteLength: number): void {
  if (kind !== 'sfx' && kind !== 'music') fail('Audio kind must be sfx or music.');
  const format = AUDIO_MEDIA_FORMATS[kind];
  if (byteLength > format.maxBytes) {
    fail(`${kind} audio exceeds the ${format.maxBytes}-byte limit; use a shorter or lower-rate file.`);
  }
}

/**
 * ONE pass over the PCM frames: duration, peak, energy, and the first/last frame
 * that rises above the silence threshold. Frames, not samples, so a stereo
 * file's silence boundaries are measured in real time rather than half of it.
 *
 * This is the whole of what the platform "hears" about an artifact, and it is
 * shared by every container: an uploaded WAV is measured here after
 * {@link parseCanonicalWav}, and a generated Ogg/Opus loop is measured here
 * after the worker's libopus decode. Two containers can therefore never drift
 * into two definitions of "loudness" or "silence".
 */
export function measureAudioPcm16(pcm: AudioPcm16): AudioPcmMeasurement {
  const { samples, channels, sampleRate } = pcm;
  if (channels !== 1 && channels !== 2) fail('Audio must be mono or stereo.');
  if (!Number.isInteger(sampleRate) || sampleRate < 8_000 || sampleRate > 48_000) {
    fail('Audio sample rate must be an integer between 8000 and 48000 Hz.');
  }
  if (samples.length === 0 || samples.length % channels !== 0) fail('Audio must contain at least one complete PCM sample frame.');
  const frames = samples.length / channels;
  let peakMagnitude = 0;
  let sumSquares = 0;
  let firstLoudFrame = -1;
  let lastLoudFrame = -1;
  for (let frame = 0; frame < frames; frame += 1) {
    let frameMagnitude = 0;
    for (let channel = 0; channel < channels; channel += 1) {
      const signedSample = samples[frame * channels + channel]!;
      const magnitude = signedSample === -32_768 ? 32_768 : Math.abs(signedSample);
      sumSquares += signedSample * signedSample;
      if (magnitude > frameMagnitude) frameMagnitude = magnitude;
    }
    if (frameMagnitude > peakMagnitude) peakMagnitude = frameMagnitude;
    if (frameMagnitude > SILENCE_MAGNITUDE) {
      if (firstLoudFrame < 0) firstLoudFrame = frame;
      lastLoudFrame = frame;
    }
  }
  const rms = Math.sqrt(sumSquares / (frames * channels));
  const durationMs = frames * 1_000 / sampleRate;
  const msPerFrame = 1_000 / sampleRate;
  const silent = firstLoudFrame < 0;
  return {
    durationMs,
    peak: peakMagnitude / 32_768,
    rmsDbfs: rms > 0
      ? Math.max(AUDIO_SILENCE_DBFS_FLOOR, 20 * Math.log10(rms / 32_768))
      : AUDIO_SILENCE_DBFS_FLOOR,
    leadingSilenceMs: silent ? durationMs : firstLoudFrame * msPerFrame,
    trailingSilenceMs: silent ? durationMs : (frames - 1 - lastLoudFrame) * msPerFrame,
  };
}

/**
 * Turn decoded PCM plus the container's own byte count into the inspection every
 * caller consumes: kind budgets, the peak ceiling and the measurements, in that
 * order. The ONE place an artifact's duration and peak are judged, so a
 * compressed container cannot acquire looser limits than an uploaded one.
 *
 * `byteLength` is the length of the STORED bytes — the WAV file, or the Ogg
 * file — never the size of the decoded PCM, because it is the stored bytes whose
 * digest identifies the artifact and whose size the runtime budgets its fetch
 * against.
 */
export function audioInspectionFromPcm16(args: {
  readonly mime: AudioMediaMime;
  readonly byteLength: number;
  readonly kind: AudioMediaKind;
  readonly pcm: AudioPcm16;
}): AudioBytesInspection {
  assertAudioByteBudget(args.kind, args.byteLength);
  const format = AUDIO_MEDIA_FORMATS[args.kind];
  const measurement = measureAudioPcm16(args.pcm);
  if (measurement.durationMs > format.maxDurationMs) {
    fail(`${args.kind} audio exceeds the ${format.maxDurationMs}ms duration limit.`);
  }
  if (measurement.peak > AUDIO_PEAK_CEILING) {
    fail(`Audio peak exceeds the maximum permitted normalized amplitude of ${AUDIO_PEAK_CEILING}.`);
  }
  return {
    mime: args.mime,
    bytes: args.byteLength,
    durationMs: measurement.durationMs,
    sampleRate: args.pcm.sampleRate,
    channels: args.pcm.channels,
    peak: measurement.peak,
    rmsDbfs: measurement.rmsDbfs,
    leadingSilenceMs: measurement.leadingSilenceMs,
    trailingSilenceMs: measurement.trailingSilenceMs,
  };
}

/**
 * Read canonical PCM16 WAV into PCM. Only a 44-byte RIFF/WAVE header followed by
 * PCM format 1, 16-bit little endian data is accepted; nothing is modified or
 * re-encoded, and samples are read byte by byte so the result does not depend on
 * the host's endianness or on the buffer's alignment.
 *
 * Structural violations fail here. Budgets and measurements are NOT applied —
 * {@link inspectAudioBytes} adds those for a stored artifact, while the worker
 * reads a music provider's WAV source through this function alone (its MP3
 * source through `mp3Source.ts`), because a transient source is allowed to be
 * larger than any stored artifact.
 */
export function parseCanonicalWav(bytes: Uint8Array): AudioPcm16 {
  if (!(bytes instanceof Uint8Array)) fail('Audio upload must be a Uint8Array.');
  if (bytes.byteLength < HEADER_BYTES) fail('WAV file is too short for the required 44-byte RIFF header.');

  if (!hasAscii(bytes, 0, 'RIFF') || !hasAscii(bytes, 8, 'WAVE')) fail('Audio must be a RIFF/WAVE file.');
  if (u32(bytes, 4) !== bytes.byteLength - 8) fail('WAV RIFF length must exactly match the upload byte length.');
  if (!hasAscii(bytes, 12, 'fmt ') || u32(bytes, 16) !== 16) fail('WAV must use the canonical 16-byte fmt chunk at offset 12.');
  if (u16(bytes, 20) !== 1) fail('WAV audio format must be PCM (format 1).');
  const channels = u16(bytes, 22);
  if (channels !== 1 && channels !== 2) fail('WAV must be mono or stereo.');
  const sampleRate = u32(bytes, 24);
  if (sampleRate < 8_000 || sampleRate > 48_000) fail('WAV sample rate must be between 8000 and 48000 Hz.');
  const blockAlign = u16(bytes, 32);
  if (u16(bytes, 34) !== 16) fail('WAV samples must be 16-bit PCM.');
  const expectedBlockAlign = channels * 2;
  if (blockAlign !== expectedBlockAlign) fail('WAV block alignment must equal channels × 2 bytes for PCM16.');
  const byteRate = u32(bytes, 28);
  if (byteRate !== sampleRate * blockAlign) fail('WAV byte rate must equal sample rate × block alignment.');
  if (!hasAscii(bytes, 36, 'data')) fail('WAV must have the canonical data chunk at offset 36.');
  const dataBytes = u32(bytes, 40);
  if (dataBytes < blockAlign) fail('WAV must contain at least one complete PCM sample frame.');
  if (dataBytes !== bytes.byteLength - HEADER_BYTES) fail('WAV data length must exactly match bytes after the 44-byte header.');
  if (dataBytes % blockAlign !== 0) fail('WAV data length must align to complete PCM sample frames.');

  const samples = new Int16Array(dataBytes / 2);
  for (let index = 0; index < samples.length; index += 1) {
    const offset = HEADER_BYTES + index * 2;
    const raw = bytes[offset]! | (bytes[offset + 1]! << 8);
    samples[index] = raw & 0x8000 ? raw - 0x10000 : raw;
  }
  return { samples, channels, sampleRate };
}

/**
 * Inspect a stored artifact's exact bytes: parse the container, apply the kind's
 * budgets and the peak ceiling, and measure the PCM. Nothing is modified.
 *
 * Only `audio/wav` is inspectable here, because that is the only container
 * `@djgg/core` can read without a codec. A generated Ogg/Opus loop is inspected
 * by the worker, which decodes it with libopus and then calls
 * {@link audioInspectionFromPcm16} — the same budgets, ceiling and measurements
 * this function applies. The caller remains responsible for hashing these exact
 * bytes and storing that digest alongside the result.
 */
export function inspectAudioBytes(bytes: Uint8Array, kind: AudioMediaKind): AudioBytesInspection {
  assertAudioByteBudget(kind, bytes instanceof Uint8Array ? bytes.byteLength : 0);
  return audioInspectionFromPcm16({
    mime: 'audio/wav',
    byteLength: bytes.byteLength,
    kind,
    pcm: parseCanonicalWav(bytes),
  });
}

/**
 * The ONE way stored audio metadata is built: measurements + a validated
 * license (+ loop points). The generated path already holds an inspection, so it
 * calls this directly rather than decoding the PCM a second time.
 */
export function audioMetadataFrom(
  inspection: AudioBytesInspection,
  kind: AudioMediaKind,
  license: unknown,
  loop?: unknown,
): AudioMediaMetadata {
  const canonicalLicense = inspectLicense(license);
  const canonicalLoop = inspectLoop(kind, loop, inspection.durationMs);
  // Projected field by field, never spread: the stored shape is compared for
  // exact equality against a fresh inspection at publication, so a measurement
  // leaking into it would invalidate every previously stored row.
  return {
    mime: inspection.mime,
    bytes: inspection.bytes,
    durationMs: inspection.durationMs,
    sampleRate: inspection.sampleRate,
    channels: inspection.channels,
    peak: inspection.peak,
    license: canonicalLicense,
    ...(canonicalLoop === undefined ? {} : canonicalLoop),
  };
}

/**
 * Inspects an exact upload: {@link inspectAudioBytes} plus the license and
 * optional loop contract, producing the metadata stored beside the digest.
 */
export function inspectAudioMedia(
  bytes: Uint8Array,
  kind: AudioMediaKind,
  license: unknown,
  loop?: unknown,
): AudioMediaMetadata {
  return audioMetadataFrom(inspectAudioBytes(bytes, kind), kind, license, loop);
}

/**
 * The UPLOAD policy on provenance: a creator may claim ownership or a license,
 * never that a provider generated the bytes. `provider-generated` is written by
 * the generation pipeline from its own request provenance, so accepting it from
 * a client would let an upload launder generated audio past that record.
 */
export function assertUploadableAudioLicense(license: AudioMediaLicense): void {
  if (license.kind === 'provider-generated') {
    fail('Uploaded audio must be creator-owned or licensed; provider-generated provenance is recorded by the generator, not claimed by an uploader.');
  }
}

/**
 * The GENERATED-candidate policy: what a paid generator must not be allowed to
 * hand back. Deliberately NOT applied to uploads — a creator uploading a quiet
 * or sparse recording is making a choice, whereas a provider returning silence,
 * an inaudible file or mostly dead air is a defect the creator should never be
 * asked to review. Judges only measurements, never taste.
 */
export function assertGeneratedAudioAcceptable(
  inspection: AudioBytesInspection,
  kind: AudioMediaKind,
): void {
  const minDurationMs = kind === 'music' ? 5_000 : 120;
  if (inspection.durationMs < minDurationMs) {
    fail(`Generated ${kind} is ${Math.round(inspection.durationMs)}ms, under the ${minDurationMs}ms floor.`);
  }
  if (inspection.leadingSilenceMs >= inspection.durationMs) {
    fail(`Generated ${kind} is silent.`);
  }
  if (inspection.rmsDbfs < -45) {
    fail(`Generated ${kind} is inaudibly quiet (${inspection.rmsDbfs.toFixed(1)} dBFS).`);
  }
  const deadAirMs = inspection.leadingSilenceMs + inspection.trailingSilenceMs;
  if (deadAirMs > inspection.durationMs / 2) {
    fail(`Generated ${kind} is more than half silence (${Math.round(deadAirMs)}ms of ${Math.round(inspection.durationMs)}ms).`);
  }
}
